import React from 'react';
import {
  Component,
  FilterCombo,
  HBox, Position,
  PuzzleButton,
  PuzzleInput
} from "puzzle"
import {
  ComboBoxChangeEvent,
  ListItemProps,
} from "@progress/kendo-react-dropdowns";
import {
  GridCellProps,
  GridColumn as Column,
  GridHeaderCellProps
} from "@progress/kendo-react-grid";
import _ from "lodash";
import KendoGridAbstract from "../../../../util/KendoGridAbstract/KendoGridAbstract";
import { GridColumnProps } from "@progress/kendo-react-grid/dist/npm/interfaces/GridColumnProps";
import {
  defaultCellRender,
  getTableTdAccessibilityProps,
  quickFilterChangeDelay
} from "../../../../util/Util";
import HeaderCell from "puzzle/components/grid/header_cell_renderer/HeaderCell";
import {
  GRADING_AND_SCORING_ITEM,
  GRID_COLUMN_ITEM, OPERATIONS_ENUM_TYPE, POST_TEM,
  PROPS,
  STATE
} from "./Types";
import TextComponent from "puzzle/components/UI/text_component/TextComponent";
import {
  MaskedTextBoxChangeEvent
} from "@progress/kendo-react-inputs/dist/npm/maskedtextbox/MaskedTextBoxProps";

class GradingAndScoringDialog extends Component<PROPS, STATE> {
  applyToAll: string = 'applyToAll'; //fake class name used to prevent trigger on event propagation
  comment: string = '';
  deletedItems: GRADING_AND_SCORING_ITEM[] = [];
  displayedPhaseName: null | string = null;

  gridConfig: GRID_COLUMN_ITEM[] = [{
    dataIndex: 'fileName'
  }, {
    dataIndex: 'grade'
  }, {
    dataIndex: 'score',
    width: 256
  }, {
    dataIndex: 'add/delete',
    width: 40
  }];

  operationsEnum: OPERATIONS_ENUM_TYPE = {
    add: 'ADD',
    del: 'DELETE'
  };

  wfSetupEnum = {
    optional: 'requiredOptional',
    readOnly: 'readOnly',
    requiredMandatory: 'requiredMandatory'
  };

  state: STATE = {
    data: [],
    filterText: '',
    searchBarText: ''
  };

  /**
   * Used for delaying form(withFormControl) change
   * @param event
   */
  delayedOnChange = _.debounce((event: MaskedTextBoxChangeEvent) => {
    const { value: filterText = '' } = event.target;
    this.setState({ filterText }, () => this.dispatch('updateGradeAndScoringDialogToolbar'))
  }, quickFilterChangeDelay);

  constructor(props: PROPS) {
    super(props);
    let {
      gradingAndScoringWfSetup,
      targetGradingAndScoring
    } = props.responseData;

    this.displayedPhaseName = props.supplierCompletedFiles ? null : props.phaseName;
    let additionalGS: number | null = null;
    let allTargetPhases = new Set();
    const isReadOnly = gradingAndScoringWfSetup === this.wfSetupEnum.readOnly;
    this.state.data = targetGradingAndScoring.map((item: GRADING_AND_SCORING_ITEM) => {
      let newItem = {
        ...item,
        initialData: true,
        old_score: item.score,
        old_grade: item.grade,
        scoreValid: true,
        gradeValid: true
      };

      if (additionalGS !== item.targetId) {
        additionalGS = item.targetId;
      } else {
        newItem.secondLevel = true;
      }

      if (isReadOnly) {
        allTargetPhases.add(item.phaseName);
      }

      return newItem;
    });

    if (isReadOnly) {
      this.displayedPhaseName = allTargetPhases.values().next().value;
      if (allTargetPhases.size > 1) {
        this.displayedPhaseName = null;
      }
    }

  };

  /**
   * Creates comments request parameters
   */
  addCommentRequestParams = () => {
    const {
      submissionId,
      responseData
    } = this.props;

    return {
      addCommentUrl: `/submissions/${submissionId}/comments/add`,
      commentParams: {
        action: "SAVED",
        activeFilters: {
          targetIds: responseData.targetGradingAndScoring.reduce((acc: number[], item: GRADING_AND_SCORING_ITEM) => {
            if (!acc.includes(item.targetId)) {
              acc.push(item.targetId)
            }
            return acc;
          }, []),
        },
        comment: this.comment
      }
    }
  };

  /**
   * Adds new row for additional grade and score
   * @param dataItem
   */
  addRow = (dataItem: GridCellProps) => {
    let data = _.cloneDeep(this.state.data);
    const lastIndex = _.findLastIndex(data, (element: GRADING_AND_SCORING_ITEM) => {
      return element.targetId === dataItem.dataItem.targetId;
    });

    data.splice(lastIndex + 1, 0, {
      ..._.omit(dataItem.dataItem, ['old_grade', 'old_score']),
      added: true,
      grade: null,
      score: null,
      secondLevel: true,
      initialData: false
    } as GRADING_AND_SCORING_ITEM);

    this.setState({ data });
  };

  /**
   * Creates column with + or - depending on secondLevel prop
   * @param dataItem
   */
  addOrDeleteColumnRender = (dataItem: GridCellProps): JSX.Element | null => {
    const {
      gradingAndScoringWfSetup,
      gradingScoringModels
    } = this.props.responseData;
    let render = null;

    if (gradingAndScoringWfSetup !== this.wfSetupEnum.readOnly && !dataItem.dataItem.readOnly) {
      const {
        phaseType,
        targetId,
        secondLevel
      } = dataItem.dataItem;
      let iconClass = 'tp-icon tp-remove';
      let handler = this.removeRow.bind(this, dataItem);
      let disabled = false;
      if (!secondLevel) {
        iconClass = 'tp-icon tp-add';
        handler = this.addRow.bind(this, dataItem);
        let gradings = _.find(gradingScoringModels, { phaseType })?.gradings || [];
        disabled = gradings.length <= this.state.data.reduce((acc: number, item: GRADING_AND_SCORING_ITEM) => {
          if (item.targetId === targetId) {
            acc += 1;
          }
          return acc;
        }, 0);
      }

      render = (
        <PuzzleButton
          iconClass={iconClass}
          disabled={disabled}
          fillMode={'flat'}
          size={'small'}
          themeColor={'primary'}
          onClick={handler}
        />
      );
    }

    return render;
  };

  /**
   * Triggered on any action on "missingGrades" dialog.
   * @param data
   */
  afterMissingGrades = (data: GRADING_AND_SCORING_ITEM[]) => {
    this.setState({
      data,
      filterText: '',
      searchBarText: ''
    });
  };

  /**
   * Apply to all handler
   * @param dataItem
   * @param type
   * @param forceChange
   */
  applyToAllHandler = (dataItem: string, type: 'grade' | 'score', forceChange: boolean = false) => {
    const value = dataItem;
    let { data } = this.state;

    if (type === 'score' || forceChange || data.every((item: GRADING_AND_SCORING_ITEM) => item?.secondLevel || _.isNull(item.old_grade))) {
      let targetId: null | number = null;
      data = data.reduce((array: GRADING_AND_SCORING_ITEM[], element: GRADING_AND_SCORING_ITEM) => {
        if (targetId !== element.targetId) {
          targetId = element.targetId;
        }

        if (!element.secondLevel && this.containsFilterValue(element)) {
          array.push({
            ...element,
            [type]: value,
            [`${type}Valid`]: true,
            ...(type === 'grade' && {
              score: null,
              scoreValid: true
            })
          })
        } else {
          if (!((element.targetId === targetId) && (element[type] === value))) {
            array.push({
              ...element
            })
          }
        }
        return array;
      }, []);

      this.setState({ data }, () => {
        this.dispatch('closeApplyToAllWarning'); //to prevent filterCombo focus error
      });
    } else {
      this.dispatch('applyToAllWarning', '', {
        dataItem,
        type
      });
    }
  };

  /**
   * On button click clear searchbar
   */
  clearSearchBar = () => this.setState({
      searchBarText: '',
      filterText: ''
  }, () => this.dispatch('updateGradeAndScoringDialogToolbar'));

  /**
   * Checks if fileName is contained in current filer text
   * @param fileName
   */
  containsFilterValue = ({ fileName }: { fileName: string }) => {
    return fileName.toLowerCase().includes(this.state.filterText.toLowerCase());
  };

  /** Grid cell render
   * @param cellProps
   */
  cellRender = (cellProps: GridCellProps): JSX.Element => {
    const {
      field
    } = cellProps;
    const tdProps: any = getTableTdAccessibilityProps(cellProps);
    let editedProps = _.cloneDeep(cellProps)
    let child;

    switch (field) {
      case 'grade':
        editedProps.className = `tp-grade-cell`;
        child = this.gradeAndScoreColumnRender('grade', cellProps);
        break;
      case 'score':
        editedProps.className = `tp-score-cell`;
        child = this.gradeAndScoreColumnRender('score', cellProps);
        break;
      case 'add/delete':
        child = this.addOrDeleteColumnRender(cellProps);
        break;
      case 'fileName':
        child = cellProps.dataItem.secondLevel ? null : this.fileNameRenderer(cellProps);
        break;
      default:
        child = cellProps.dataItem.secondLevel ? null : defaultCellRender(this.translate, () => {
        }, cellProps);
        break;
    }

    return <td {...tdProps} className={`${editedProps.className} tp-sortable`}>{child}</td>;
  };


  /**
   * Clear all handler
   */
  clearAllHandler = () => {
    let data = this.state.data.reduce((array: GRADING_AND_SCORING_ITEM[], element: GRADING_AND_SCORING_ITEM) => {
      let item = { ...element };
      if (!element.secondLevel) {
        if (this.containsFilterValue(element)) {
          item = {
            ...element,
            grade: null,
            gradeValid: true,
            score: null,
            scoreValid: true
          }
        }
        array.push(item);
      } else {
        if (element.old_grade) {
          this.deletedItems.push(item)
        }
      }

      return array;
    }, []);

    this.setState({ data }, () => this.dispatch('updateGradeAndScoringDialogToolbar'));
  };

  /**
   * Returns flags required for setting hidden and disabled prop for footer buttons
   */
  getFooterButtonsConfig = () => {
    const {
      responseData
    } = this.props;
    let isReadOnly = responseData.gradingAndScoringWfSetup === this.wfSetupEnum.readOnly || _.get(responseData, 'targetGradingAndScoring[0].readOnly');
    let disableClearAll = !isReadOnly && !_.find(this.state.data, (item: GRADING_AND_SCORING_ITEM) => {
      return this.containsFilterValue(item) && (item.grade || item.score);
    });

    return {
      isReadOnly,
      disableClearAll
    }
  };

  /**
   * File name cell renderer.
   * @param cellProps
   */
  fileNameRenderer = (cellProps: GridCellProps) => {
    const fileName = cellProps.dataItem.fileName;
    return (
      <div>
        <PuzzleButton
          aria-label={this.translate("SRL-openFilePathDialog", { fileName: fileName })}
          titleText={this.translate("SRL-openFilePathDialog", { fileName: fileName })}
          onClick={() => this.dispatch('filePathGradingAndScoring', '', { fileName })}
          fillMode={'flat'}
          themeColor={'primary'}
          size={'small'}
          iconClass={'tp-icon tp-file-path'}
          tooltipPosition={'right'}
          withTooltip
        />
        <span className={'tp-text-ellipsis'}>{fileName}</span>
      </div>
    )
  };

  /**
   * Grade and score FilterCombo on change handler
   * @param dataItem
   * @param type
   * @param event
   */
  gradeAndScoreChange = (dataItem: GridCellProps, type: 'grade' | 'score', event: ComboBoxChangeEvent): void => {
    if (!_.get(event, 'nativeEvent.target.classList')?.contains(this.applyToAll)) {
      const { data: currentStateData } = this.state;
      const { value } = event || null;
      let index = _.findIndex(currentStateData, (item) => item === dataItem.dataItem);
      let data = _.cloneDeep(currentStateData);
      let item = data[index];
      if (item) {
        const validityProp = `${type}Valid` as 'gradeValid' | 'scoreValid';
        item[validityProp] = _.isNull(value) || _.isString(value);
        if (type === 'grade') {
          item['score'] = null;
          item['scoreValid'] = true;
        }
        item[type] = value;
      }

      this.setState({ data }, () => this.dispatch('updateGradeAndScoringDialogToolbar'));
    }
  };

  /**
   * Grid header cell render
   * @param field
   * @param thProps
   */
  headerRender = (field: string, thProps: GridHeaderCellProps): JSX.Element | null => {
    let render: JSX.Element | null = <HeaderCell
      {...thProps as any}
    />;

    if (['grade', 'score'].includes(field)) {
      const {
        actionName,
        responseData
      } = this.props;
      const showMandatoryAsterisk = this.wfSetupEnum.requiredMandatory === responseData.gradingAndScoringWfSetup && _.includes(['promote', 'demote'], actionName);
      render = <HeaderCell
        {...thProps as any}
        children={<span
          className='k-text-ellipsis'
          title={thProps.title}
        >{showMandatoryAsterisk && <span className={'tp-color-error k-ml-1'}>*</span>}
     </span>
        }
      />;
    } else if (field === 'add/delete') {
      render = null;
    }

    return render;
  };

  /**
   * Check is data hanged
   */
  isDataChanged = (): boolean => {
    const flag: boolean = !!this.deletedItems.length || this.state.data.some(item => _.get(item, 'add') || (item.initialData && ((item.old_grade !== item.grade) || (item.old_score !== item.score))));
    return flag;
  };

  /**
   * Method that renders files grid columns
   * @param column
   */
  renderColumn = (column: GRID_COLUMN_ITEM): JSX.Element => {
    let dataIndex: string = column.dataIndex;
    let columnProps: GridColumnProps = {
      field: dataIndex,
      headerCell: this.headerRender.bind(this, dataIndex),
      title: this.translate(`columnHeaders.${dataIndex}`),
      cell: this.cellRender,
      ...(column.width && { width: column.width })
    };

    return <Column
      key={dataIndex}
      {...columnProps}
    />;
  };

  /**
   * Removes extra row (secondLevel)
   * @param dataItem
   */
  removeRow = (dataItem: GridCellProps) => {
    let data = _.cloneDeep(this.state.data);
    let deletedItem = data.splice(dataItem.dataIndex, 1);
    if (_.get(deletedItem[0], 'initialData')) {
      this.deletedItems.push(deletedItem[0]);
    }

    this.setState({ data });
  };

  /**
   * Item render
   * @param type
   * @param rowItem
   * @param li
   * @param itemProps
   */
  itemRender = (type: 'grade' | 'score', rowItem: GRADING_AND_SCORING_ITEM, li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps): JSX.Element => {
    const applyToAllText = this.translate('applyToAll');
    const dataItem = itemProps.dataItem;
    let disabledClass = "";
    if (type === 'grade') {
      let usedGrades = this.state.data.reduce((array: (string | null)[], item: GRADING_AND_SCORING_ITEM) => {
        if (item.targetId === rowItem.targetId) {
          array.push(item.grade);
        }
        return array;
      }, []);
      if (usedGrades.length && (_.includes(usedGrades, dataItem))) {
        disabledClass = "k-disabled";
      }
    }

    const itemChildren = (
      <HBox
        alignItemsCenter
        className={'tp-100'}
        gap={2}
        justifyContentBetween
      >
        {dataItem}
        {!rowItem.secondLevel && this.props.phaseName &&
          <PuzzleButton
            className={`tp-text-underline tp-color-link  tp-font-size-sm ${this.applyToAll}`}
            fillMode={'flat'}
            themeColor={'primary'}
            size={'small'}
            onClick={this.applyToAllHandler.bind(this, dataItem, type, false)}
          >
            {applyToAllText}
          </PuzzleButton>
        }
      </HBox>
    );

    return React.cloneElement(li, {
      ...li.props,
      className: `${li.props.className} ${disabledClass} tp-list-item-with-button`
    }, itemChildren);
  };

  /**
   * Grade and score colum render
   * @param type
   * @param dataItem
   */
  gradeAndScoreColumnRender = (type: 'grade' | 'score', dataItem: GridCellProps): JSX.Element => {
    let { dataItem: item } = dataItem;
    let { phaseType } = item;

    const {
      gradingScoringModels,
      gradingAndScoringWfSetup
    } = this.props.responseData;

    const modelProp = type === 'grade' ? 'gradings' : 'scorings';
    let data = _.find(gradingScoringModels, { phaseType })?.[modelProp] || [];
    const isReadOnly = gradingAndScoringWfSetup === this.wfSetupEnum.readOnly || item.readOnly;
    const readOnlyCls = isReadOnly ? 'read-only' : '';

    let dropDownProps = {
      data,
      disabled: isReadOnly,
      value: item[type],
      valid: item[`${type}Valid`]
    };


    return (
      <FilterCombo
        itemRender={this.itemRender.bind(this, type, dataItem.dataItem)}
        onChange={this.gradeAndScoreChange.bind(this, dataItem, type)}
        className={readOnlyCls}
        placeholder={this.translate(`${type}_placeholder`)}
        valueAsSimpleType={true}
        {...dropDownProps}
      />
    );
  };

  /**
   * Grading and scoring save button handler
   */
  saveButtonHandler = () => {
    let {
      allValid,
      data
    } = this.validateData(true);

    this.setState({ data }, this.continueWithSave.bind(this, allValid, true));
  };

  /**
   * Complete/Return action handler
   * @param action
   */
  completeReturnHandler = (action: "COMPLETED" | "DEMOTED") => {
    let {
      allValid,
      data
    } = this.validateData();

    if (!allValid) {
      this.dispatch('missingGradesWarning', Position.end, { gsValidatedData: data });
    } else {
      let gsDataForComplete: any = this.continueWithSave(true, false);
      if (_.get(gsDataForComplete, 'commentParams.action')) {
        gsDataForComplete.commentParams.action = action;
      }

      this.dispatch('closeGsAndContinuePromoteDemote', Position.end, { ...gsDataForComplete });
    }
  };

  /**
   * Continues save flow if all records are valid
   * @param valid
   * @param forSave
   */
  continueWithSave = (valid: boolean, forSave: boolean = true) => {
    if (valid) {
      const {
        add,
        del
      } = this.operationsEnum;

      const deletedTargets = this.deletedItems.map((item: GRADING_AND_SCORING_ITEM) => {
        const {
          old_grade,
          old_score,
          phaseName,
          targetId
        } = item;
        return {
          action: del,
          grade: old_grade,
          phaseName,
          score: old_score,
          targetId,
        }
      });

      let gradedFileCount = 0;
      const addedAndEditedTargets = this.state.data.reduce((array: POST_TEM[], item: GRADING_AND_SCORING_ITEM) => {
        const {
          added,
          grade,
          initialData,
          old_grade = null,
          old_score = null,
          phaseName,
          score,
          targetId
        } = item;

        if (initialData && (old_score !== score || old_grade !== grade)) { //edited saved item
          if (grade === null && score === null) { //counts as deleted
            array.push({
              action: del,
              grade: old_grade,
              phaseName,
              score: old_score,
              targetId
            });
          } else if (old_grade === null && old_score === null) {//count as added
            if (!_.find(array, { targetId })) {
              gradedFileCount++;
            }

            array.push({
              action: add,
              grade,
              phaseName,
              score,
              targetId
            })
          } else {//counts as edited
            if (!_.find(array, { targetId })) {
              gradedFileCount++;
            }

            array.push({
              action: del,
              grade: old_grade,
              phaseName,
              score: old_score,
              targetId
            }, {
              action: add,
              grade,
              phaseName,
              score,
              targetId
            });
          }
        } else if (added && grade !== null && score !== null) { // newly added items
          if (!_.find(array, { targetId })) {
            gradedFileCount++;
          }
          array.push({
            action: add,
            grade,
            phaseName,
            score,
            targetId
          })
        }

        return array;
      }, []);

      const gsSaveRequestData = {
        endpoint: '/submissions/saveGradingAndScoring',
        gradedFileCount,
        saveParams: {
          phaseName: this.props.phaseName,
          submissionId: this.props.submissionId,
          targetGradingAndScoring: [
            ...deletedTargets,
            ...addedAndEditedTargets
          ]
        },
        ...(this.comment && this.addCommentRequestParams())
      };

      if (forSave) {
        this.dispatch('saveGradingAndScoring', '', { gsSaveRequestData })
      } else {
        return { gsSaveRequestData };
      }
    }
  };

  /**
   * Grading and Scoring comment setter
   * @param comment
   */
  setComment = (comment: string) => {
    this.comment = comment;
  };

  /**
   * Search bar change handler
   * @param event
   */
  searchBarChange = (event: MaskedTextBoxChangeEvent) => {
    const { value: searchBarText = '' } = event.target;
    this.setState({ searchBarText });
    this.delayedOnChange(event);
  };

  /**
   * Creates validation value for every record, ald calculates are all fields valid
   * @param forSave
   */
  validateData = (forSave: boolean = false) => {
    let allValid = true;
    let data = this.state.data.map((item: GRADING_AND_SCORING_ITEM) => {
      if (!forSave || this.containsFilterValue(item)) {
        item = {
          ...item,
          gradeValid: forSave || item.secondLevel ? _.isString(item.grade) || _.isNull(item.score) : _.isString(item.grade),
          scoreValid: forSave || item.secondLevel ? _.isString(item.score) || _.isNull(item.grade) : _.isString(item.score)
        };
      }

      if (allValid) {
        allValid = item.gradeValid && item.scoreValid;
      }

      return item;
    });

    return {
      allValid,
      data
    }
  };

  renderBody(): JSX.Element {
    const {
      filesCount,
      responseData
    } = this.props;
    let {
      data,
      searchBarText
    } = this.state;

    return (
      <>
        <HBox
          className={'tp-garde-and-score-topbar'}
          alignItemsCenter
          gap={2}
          justifyContentBetween
          paddingX
        >
          <TextComponent size={'md'} thickness={'medium'}>
            {this.displayedPhaseName}
          </TextComponent>
          <span className={'tp-garde-and-score-topbar-searchbox'}>
            <PuzzleInput
              label={this.translate('searchGradeAndScore')}
              labelClassName={'sr-only'}
              actionBtnSettings={[{
                ariaLabel: this.translate('SRL-clearSearchBar'),
                buttonType: 'delete',
                onClick: this.clearSearchBar,
                fillMode: 'flat',
                titleText: this.translate('SRL-clearSearchBar'),
                withTooltip: true
              }]}
              id='filterGradeAndScore'
              name='filterGradeAndScore'
              onChange={this.searchBarChange}
              placeholder={this.translate("searchBarPlaceholder")}
              value={searchBarText}
              searchField
            />
          </span>
          {responseData.displayAddCommentButton && !(responseData.gradingAndScoringWfSetup === this.wfSetupEnum.readOnly || _.get(responseData, 'targetGradingAndScoring[0].readOnly')) ?
            <span>
            <PuzzleButton
              themeColor={'primary'}
              onClick={() => this.dispatch('openGradingAndScoringComment', '', {
                comment: this.comment,
                filesCount,
                setComment: this.setComment
              })}
            >
              {this.translate("addComment")}
            </PuzzleButton>
          </span> : null
          }
        </HBox>
        <KendoGridAbstract
          captionText={this.translate("gridCaption")}
          className={'tp-no-border'}
          data={data.filter((item: GRADING_AND_SCORING_ITEM) => {
            return this.containsFilterValue(item);
          })}
          rowHeight={40}
        >
          {this.gridConfig.map(this.renderColumn)}
        </KendoGridAbstract>
      </>
    );
  }
}

export default GradingAndScoringDialog;
